/**
* @license Apache-2.0
*
* Copyright (c) 2022 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils-define-read-only-property';


// MAIN //

/**
* Namespace.
*
* @namespace ns
*/
var ns = {};

/**
* @name contains
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/assert/contains}
*/
import contains from '@stdlib/array-base-assert-contains';
setReadOnly( ns, 'contains', contains );

/**
* @name hasAlmostEqualValues
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/assert/has-almost-equal-values}
*/
import hasAlmostEqualValues from '@stdlib/array-base-assert-has-almost-equal-values';
setReadOnly( ns, 'hasAlmostEqualValues', hasAlmostEqualValues );

/**
* @name hasAlmostSameValues
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/assert/has-almost-same-values}
*/
import hasAlmostSameValues from '@stdlib/array-base-assert-has-almost-same-values';
setReadOnly( ns, 'hasAlmostSameValues', hasAlmostSameValues );

/**
* @name hasEqualValues
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/assert/has-equal-values}
*/
import hasEqualValues from '@stdlib/array-base-assert-has-equal-values';
setReadOnly( ns, 'hasEqualValues', hasEqualValues );

/**
* @name hasEqualValuesIndexed
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/assert/has-equal-values-indexed}
*/
import hasEqualValuesIndexed from '@stdlib/array-base-assert-has-equal-values-indexed';
setReadOnly( ns, 'hasEqualValuesIndexed', hasEqualValuesIndexed );

/**
* @name hasSameValues
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/assert/has-same-values}
*/
import hasSameValues from '@stdlib/array-base-assert-has-same-values';
setReadOnly( ns, 'hasSameValues', hasSameValues );

/**
* @name isAccessorArray
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/assert/is-accessor-array}
*/
import isAccessorArray from '@stdlib/array-base-assert-is-accessor-array';
setReadOnly( ns, 'isAccessorArray', isAccessorArray );

/**
* @name isBooleanDataType
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/assert/is-boolean-data-type}
*/
import isBooleanDataType from '@stdlib/array-base-assert-is-boolean-data-type';
setReadOnly( ns, 'isBooleanDataType', isBooleanDataType );

/**
* @name isBooleanArray
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/assert/is-booleanarray}
*/
import isBooleanArray from '@stdlib/array-base-assert-is-booleanarray';
setReadOnly( ns, 'isBooleanArray', isBooleanArray );

/**
* @name isByteOrder
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/assert/is-byte-order}
*/
import isByteOrder from '@stdlib/array-base-assert-is-byte-order';
setReadOnly( ns, 'isByteOrder', isByteOrder );

/**
* @name isComplexFloatingPointDataType
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/assert/is-complex-floating-point-data-type}
*/
import isComplexFloatingPointDataType from '@stdlib/array-base-assert-is-complex-floating-point-data-type';
setReadOnly( ns, 'isComplexFloatingPointDataType', isComplexFloatingPointDataType );

/**
* @name isComplexTypedArray
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/assert/is-complex-typed-array}
*/
import isComplexTypedArray from '@stdlib/array-base-assert-is-complex-typed-array';
setReadOnly( ns, 'isComplexTypedArray', isComplexTypedArray );

/**
* @name isComplex64Array
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/assert/is-complex64array}
*/
import isComplex64Array from '@stdlib/array-base-assert-is-complex64array';
setReadOnly( ns, 'isComplex64Array', isComplex64Array );

/**
* @name isComplex128Array
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/assert/is-complex128array}
*/
import isComplex128Array from '@stdlib/array-base-assert-is-complex128array';
setReadOnly( ns, 'isComplex128Array', isComplex128Array );

/**
* @name isDataType
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/assert/is-data-type}
*/
import isDataType from '@stdlib/array-base-assert-is-data-type';
setReadOnly( ns, 'isDataType', isDataType );

/**
* @name isFloatingPointDataType
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/assert/is-floating-point-data-type}
*/
import isFloatingPointDataType from '@stdlib/array-base-assert-is-floating-point-data-type';
setReadOnly( ns, 'isFloatingPointDataType', isFloatingPointDataType );

/**
* @name isIntegerDataType
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/assert/is-integer-data-type}
*/
import isIntegerDataType from '@stdlib/array-base-assert-is-integer-data-type';
setReadOnly( ns, 'isIntegerDataType', isIntegerDataType );

/**
* @name isMostlySafeDataTypeCast
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/assert/is-mostly-safe-data-type-cast}
*/
import isMostlySafeDataTypeCast from '@stdlib/array-base-assert-is-mostly-safe-data-type-cast';
setReadOnly( ns, 'isMostlySafeDataTypeCast', isMostlySafeDataTypeCast );

/**
* @name isNumericDataType
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/assert/is-numeric-data-type}
*/
import isNumericDataType from '@stdlib/array-base-assert-is-numeric-data-type';
setReadOnly( ns, 'isNumericDataType', isNumericDataType );

/**
* @name isRealDataType
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/assert/is-real-data-type}
*/
import isRealDataType from '@stdlib/array-base-assert-is-real-data-type';
setReadOnly( ns, 'isRealDataType', isRealDataType );

/**
* @name isRealFloatingPointDataType
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/assert/is-real-floating-point-data-type}
*/
import isRealFloatingPointDataType from '@stdlib/array-base-assert-is-real-floating-point-data-type';
setReadOnly( ns, 'isRealFloatingPointDataType', isRealFloatingPointDataType );

/**
* @name isSafeDataTypeCast
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/assert/is-safe-data-type-cast}
*/
import isSafeDataTypeCast from '@stdlib/array-base-assert-is-safe-data-type-cast';
setReadOnly( ns, 'isSafeDataTypeCast', isSafeDataTypeCast );

/**
* @name isSameKindDataTypeCast
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/assert/is-same-kind-data-type-cast}
*/
import isSameKindDataTypeCast from '@stdlib/array-base-assert-is-same-kind-data-type-cast';
setReadOnly( ns, 'isSameKindDataTypeCast', isSameKindDataTypeCast );

/**
* @name isSignedIntegerDataType
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/assert/is-signed-integer-data-type}
*/
import isSignedIntegerDataType from '@stdlib/array-base-assert-is-signed-integer-data-type';
setReadOnly( ns, 'isSignedIntegerDataType', isSignedIntegerDataType );

/**
* @name isSortedAscending
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/assert/is-sorted-ascending}
*/
import isSortedAscending from '@stdlib/array-base-assert-is-sorted-ascending';
setReadOnly( ns, 'isSortedAscending', isSortedAscending );

/**
* @name isUnsignedIntegerDataType
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/assert/is-unsigned-integer-data-type}
*/
import isUnsignedIntegerDataType from '@stdlib/array-base-assert-is-unsigned-integer-data-type';
setReadOnly( ns, 'isUnsignedIntegerDataType', isUnsignedIntegerDataType );


// EXPORTS //

export default ns;
